'use client'

import React from 'react'
import { Modal } from 'design-configuration/components'
import { XIcon } from 'design-configuration/components/icons'
import { SoloDisplay } from './solo-display'
import { ProjectDisplayFragment, SoloViewerOobTokenFragment } from '@/gql/generated/graphql'
import { PaginationDirection } from '@components/pagination-button'
import { SoloViewerToken } from '@utils/common'
import { useSoloViewer } from '../provider'

export const TokenSoloViewer = (): React.ReactElement => {
  const { soloViewerData, setSoloViewerData } = useSoloViewer()
  const { token, tokenList } = soloViewerData

  const onDismiss = () => {
    setSoloViewerData({ token: undefined, tokenList: undefined })
  }

  const findTokenIndex = (token?: SoloViewerToken): number | undefined => {
    if (!token) {
      return undefined
    }
    return tokenList?.findIndex((t) => t.token_id === token.token_id)
  }

  const [tokenIndex, setTokenIndex] = React.useState<number | undefined>(undefined)

  // We need these refs for the keyboard event listener to work
  const tokenIndexRef = React.useRef<number | undefined>(tokenIndex)
  const tokenListRef = React.useRef<SoloViewerToken[] | undefined>(tokenList)

  React.useEffect(() => {
    setTokenIndex(findTokenIndex(token))
  }, [JSON.stringify(token), token?.token_id])

  React.useEffect(() => {
    tokenIndexRef.current = tokenIndex
  }, [tokenIndex])
  React.useEffect(() => {
    tokenListRef.current = tokenList
  }, [tokenList])

  const updateTokenIndex = (direction: PaginationDirection) => {
    if (!tokenListRef.current) {
      return
    }
    const currentTokenIndex = tokenIndexRef.current ?? 0
    let newIndex = (currentTokenIndex ?? 0) + (direction === PaginationDirection.Right ? 1 : -1)

    const tokens = tokenListRef.current
    if (newIndex < 0) {
      newIndex = tokens.length - 1
    } else if (newIndex > tokens.length - 1) {
      newIndex = 0
    }
    const newToken = tokens[newIndex]

    setSoloViewerData({ token: newToken, tokenList: tokens })
  }

  const selectedToken = tokenList && tokenIndex !== undefined && tokenIndex !== -1 ? tokenList[tokenIndex] : token

  const handleKeyPress = (event: any) => {
    switch (event.key) {
      case 'ArrowLeft':
        updateTokenIndex(PaginationDirection.Left)
        break
      case 'ArrowRight':
        updateTokenIndex(PaginationDirection.Right)
        break
      case 'Escape':
        onDismiss()
        break
      default:
        break
    }
  }

  React.useEffect(() => {
    window.addEventListener('keydown', handleKeyPress)

    return () => {
      window.removeEventListener('keydown', handleKeyPress)
    }
  }, [])

  const selectedTokenProject = selectedToken?.project as ProjectDisplayFragment

  return (
    <Modal
      modalType="full"
      show={token !== undefined && selectedTokenProject !== undefined}
      dismissOnClickOutside
      onDismiss={() => {
        onDismiss()
      }}
    >
      <button
        className="p-xs absolute right-0 top-0 z-[80] flex items-center justify-center"
        onClick={() => onDismiss()}
      >
        <XIcon className="fill-on-surface-variant w-sm h-sm" />
      </button>
      <div
        className="px-md pt-md pb-sm relative h-[100dvh]"
        onClick={() => {
          onDismiss()
        }}
      >
        {selectedToken && selectedTokenProject ? (
          <SoloDisplay
            token={selectedToken}
            project={selectedTokenProject}
            numTokens={tokenList?.length ?? 0}
            onChangeToken={(direction) => updateTokenIndex(direction)}
          />
        ) : null}
      </div>
    </Modal>
  )
}

export const isOob = (token: SoloViewerToken): token is SoloViewerOobTokenFragment => {
  return !(!token || 'features' in token)
}
